import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ShoppingBag, Minus, Plus, X } from "lucide-react";
import api, { resolveImage } from "../lib/api";

const stagger = { hidden: {}, show: { transition: { staggerChildren: 0.06 } } };
const fadeUp = {
  hidden: { opacity: 0, y: 18 },
  show: { opacity: 1, y: 0, transition: { duration: 0.5, ease: [0.22, 1, 0.36, 1] } },
};

export default function Shop() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [qtyMap, setQtyMap] = useState({});
  const [sizeMap, setSizeMap] = useState({});
  const [pendingBuyId, setPendingBuyId] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    api
      .get("/shop/products")
      .then((r) => {
        setItems(r.data);
        setQtyMap(Object.fromEntries(r.data.map((p) => [p.id, 1])));
        setSizeMap(Object.fromEntries(r.data.map((p) => [p.id, ""])));
      })
      .finally(() => setLoading(false));
  }, []);

  const setQty = (id, delta) =>
    setQtyMap((m) => ({ ...m, [id]: Math.max(1, Math.min(10, (m[id] || 1) + delta)) }));

  const pickSize = (id, size) =>
    setSizeMap((s) => ({ ...s, [id]: size }));

  const onBuyClick = (id) => {
    const size = sizeMap[id];
    if (!size) {
      alert("Please pick a size first.");
      return;
    }
    setPendingBuyId(id);
  };

  const confirmAndProceed = () => {
    if (!pendingBuyId) return;
    const size = sizeMap[pendingBuyId];
    const qty = qtyMap[pendingBuyId] || 1;
    setPendingBuyId(null);
    navigate(`/shop/${pendingBuyId}?size=${size}&qty=${qty}`);
  };

  return (
    <div data-testid="shop-page" className="px-6 md:px-12 lg:px-20 py-20 md:py-28">
      {/* In-progress banner */}
      <div
        data-testid="shop-in-progress-banner"
        role="status"
        className="mb-12 md:mb-16 border-2 border-black bg-[#FFD600] px-5 md:px-8 py-5 md:py-6 flex items-start gap-4"
      >
        <span className="font-display font-black text-3xl md:text-4xl leading-none shrink-0">⚠</span>
        <div className="font-mono text-sm leading-relaxed text-black">
          <p className="font-display font-black text-lg md:text-xl mb-1">
            Shop in progress — not yet active.
          </p>
          <p>
            This page is still being built. Products listed here are{" "}
            <strong>previews</strong> and the checkout is in <strong>test mode only</strong> — we
            can't fulfil real orders yet, and no real cards will be charged.
          </p>
          <p className="mt-2">
            We'll go live very soon. Follow us on{" "}
            <a
              href="https://www.instagram.com/shopxemi/"
              target="_blank"
              rel="noopener noreferrer"
              className="underline hover:text-[#E6007A]"
            >
              Instagram
            </a>{" "}
            for the launch — or{" "}
            <Link to="/contact" className="underline hover:text-[#E6007A]">
              send us a message
            </Link>{" "}
            to be added to the early-access list.
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-12 gap-8 mb-16 items-end">
        <div className="md:col-span-8">
          <p className="font-mono text-xs uppercase tracking-[0.3em] text-[#71717A] mb-6">
            Shop · {items.length} {items.length === 1 ? "piece" : "pieces"}
          </p>
          <h1 className="font-display font-black text-6xl md:text-8xl leading-[0.85] tracking-tighter">
            Wear
            <br />
            <span className="relative inline-block">
              the press
              <span className="absolute -bottom-3 left-0 h-3 w-full bg-[#FFD600] -z-10" />
            </span>
            <span className="text-[#E6007A]">.</span>
          </h1>
        </div>
        <div className="md:col-span-4">
          <p className="font-mono text-sm leading-relaxed text-[#27272A]">
            Limited DTF & sublimation drops printed in our Cornwall studio.
            UK shipping flat £3.99. Pay with card via secure Stripe checkout.
          </p>
        </div>
      </div>

      {loading ? (
        <p className="font-mono text-sm text-[#71717A]">Loading…</p>
      ) : items.length === 0 ? (
        <div data-testid="shop-empty" className="border border-dashed border-black/15 px-6 py-24 text-center">
          <ShoppingBag size={32} className="mx-auto mb-4" />
          <p className="font-display font-black text-3xl mb-2">Drop coming soon.</p>
          <p className="font-mono text-sm text-[#71717A]">
            We're prepping the first capsule. Follow us on Instagram for the launch.
          </p>
        </div>
      ) : (
        <motion.div
          variants={stagger}
          initial="hidden"
          animate="show"
          className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4 md:gap-5"
        >
          {items.map((item) => {
            const qty = qtyMap[item.id] || 1;
            return (
              <motion.div
                key={item.id}
                variants={fadeUp}
                data-testid={`shop-card-${item.id}`}
                className="group border border-black/10 bg-white flex flex-col"
              >
                <button
                  type="button"
                  onClick={() => navigate(`/shop/${item.id}`)}
                  data-testid={`shop-card-image-${item.id}`}
                  className="block w-full text-left"
                  aria-label={`View ${item.title}`}
                >
                  <div className="aspect-square overflow-hidden bg-[#F4F4F5]">
                    <img
                      src={resolveImage(item.image_url)}
                      alt={item.title}
                      loading="lazy"
                      decoding="async"
                      style={{ filter: "contrast(1.04) saturate(1.06)" }}
                      className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-[1.04]"
                    />
                  </div>
                </button>

                <div className="p-3 md:p-4 flex flex-col gap-3 flex-1">
                  <div className="flex items-start justify-between gap-2">
                    <p
                      className="font-display font-black text-sm md:text-base leading-tight line-clamp-2"
                      title={item.title}
                    >
                      {item.title}
                    </p>
                    <span className="font-display font-black text-base md:text-lg shrink-0">
                      £{item.price_gbp.toFixed(2)}
                    </span>
                  </div>

                  {/* Size chips */}
                  <div>
                    <p className="font-mono text-[9px] uppercase tracking-[0.25em] text-[#71717A] mb-1.5">
                      Size
                    </p>
                    <div className="flex flex-wrap gap-1">
                      {item.sizes.map((s) => {
                        const selected = sizeMap[item.id] === s;
                        return (
                          <button
                            key={s}
                            type="button"
                            onClick={() => pickSize(item.id, s)}
                            data-testid={`card-size-${item.id}-${s}`}
                            className={`min-w-[28px] h-7 px-1.5 font-mono text-[10px] font-semibold border transition-colors ${
                              selected
                                ? "bg-black text-white border-black"
                                : "bg-white text-black border-black/15 hover:border-black"
                            }`}
                            aria-pressed={selected}
                          >
                            {s}
                          </button>
                        );
                      })}
                    </div>
                  </div>

                  <div className="mt-auto flex items-center gap-2">
                    <div className="flex items-center border border-black/15 shrink-0 opacity-50 pointer-events-none">
                      <span className="w-8 h-9 inline-flex items-center justify-center"><Minus size={12} /></span>
                      <span className="w-8 h-9 inline-flex items-center justify-center font-mono text-xs">{qty}</span>
                      <span className="w-8 h-9 inline-flex items-center justify-center"><Plus size={12} /></span>
                    </div>
                    <button
                      type="button"
                      disabled
                      data-testid={`card-buy-${item.id}`}
                      title="Shop opens soon"
                      className="flex-1 bg-[#F4F4F5] text-[#71717A] border border-black/15 px-3 h-9 font-mono text-[10px] uppercase tracking-[0.2em] inline-flex items-center justify-center gap-1.5 cursor-not-allowed"
                    >
                      <ShoppingBag size={12} /> Coming soon
                    </button>
                  </div>
                </div>
              </motion.div>
            );
          })}
        </motion.div>
      )}

      {/* Refund Policy */}
      <section
        data-testid="shop-refund-policy"
        id="refund-policy"
        className="mt-24 md:mt-32 border-t border-black/10 pt-16 md:pt-20"
      >
        <div className="grid grid-cols-1 md:grid-cols-12 gap-8 md:gap-12">
          <div className="md:col-span-5">
            <p className="font-mono text-xs uppercase tracking-[0.3em] text-[#71717A] mb-4">
              Returns & refunds
            </p>
            <h2 className="font-display font-black text-4xl md:text-5xl leading-[0.95]">
              14 days
              <br />
              to change
              <br />
              your mind<span className="text-[#E6007A]">.</span>
            </h2>
          </div>
          <div className="md:col-span-7 space-y-5 font-mono text-sm md:text-base leading-relaxed text-[#27272A]">
            <p>
              Our return policy is valid for <strong className="text-black">14 days</strong> from
              the date your order is delivered. If you'd like to return or exchange a piece, just
              get in touch within that window and we'll send you the next steps.
            </p>
            <p>
              Items must come back unworn, unwashed and in their original condition. We're not able
              to refund printed-to-order or personalised pieces unless there's a print defect.
            </p>
            <p>
              <strong className="text-black">Outside the 14 days?</strong> Drop us a line anyway —
              in some cases we can still make a proper adjustment, especially if there's an issue
              with print quality or sizing. We're a small Cornwall studio, not a mega-retailer, and
              we'll always try to make it right.
            </p>
            <div className="pt-4 border-t border-black/10">
              <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#71717A] mb-2">
                Refund requests · contact
              </p>
              <a
                href="mailto:Shopxemi@gmail.com?subject=Refund%20request"
                data-testid="refund-email-link"
                className="font-display font-black text-2xl md:text-3xl text-black hover:text-[#E6007A] transition-colors break-all"
              >
                Shopxemi@gmail.com
              </a>
              <p className="font-mono text-xs text-[#71717A] mt-3">
                Include your order reference (from your confirmation email) and a quick note about
                what's up — we'll reply within 24 hours.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Refund-policy acknowledgement modal */}
      {pendingBuyId && (
        <div
          data-testid="refund-policy-modal"
          className="fixed inset-0 z-[100] flex items-center justify-center px-4"
          role="dialog"
          aria-modal="true"
          aria-labelledby="refund-modal-title"
        >
          <button
            type="button"
            aria-label="Close"
            onClick={() => setPendingBuyId(null)}
            className="absolute inset-0 bg-black/60 backdrop-blur-sm"
          />
          <div className="relative bg-white border border-black w-full max-w-md shadow-2xl">
            <div className="bg-black text-white px-6 py-4 flex items-center justify-between">
              <p id="refund-modal-title" className="font-mono text-[11px] uppercase tracking-[0.25em]">
                Quick note · 14-day policy
              </p>
              <button
                type="button"
                onClick={() => setPendingBuyId(null)}
                aria-label="Close"
                data-testid="refund-modal-close"
                className="text-white hover:text-[#FFD600] transition-colors"
              >
                <X size={18} />
              </button>
            </div>
            <div className="p-6 md:p-8 space-y-4">
              <h3 className="font-display font-black text-3xl leading-[0.95]">
                14 days to change your mind<span className="text-[#E6007A]">.</span>
              </h3>
              <p className="font-mono text-sm leading-relaxed text-[#27272A]">
                Returns are accepted within <strong className="text-black">14 days</strong> of
                delivery, items unworn and in original condition. Outside that, drop us a line —
                in some cases we can still make a proper adjustment.
              </p>
              <p className="font-mono text-xs text-[#71717A]">
                Refund requests:{" "}
                <a
                  href="mailto:Shopxemi@gmail.com?subject=Refund%20request"
                  className="text-[#E6007A] hover:underline"
                >
                  Shopxemi@gmail.com
                </a>
              </p>
              <div className="flex gap-3 pt-2">
                <button
                  type="button"
                  onClick={() => setPendingBuyId(null)}
                  data-testid="refund-modal-cancel"
                  className="bg-white text-black border border-black/15 px-5 h-11 font-mono text-xs uppercase tracking-[0.2em] hover:border-black"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={confirmAndProceed}
                  data-testid="refund-modal-ok"
                  className="btn-ink flex-1 bg-black text-white px-5 h-11 font-mono text-xs uppercase tracking-[0.2em]"
                >
                  OK, continue →
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
